/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HistoryItems from "./HistoryItems";
import { formatMonth } from "../../Utils/Utility";

/**
 * HistoryPagination component
 * Description: This component is used to display the history items and paginated
 */
function HistoryPagination({ data }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page");

  const theme = useTheme();
  const [ currentPage, setCurrentPage ] = useState(parseInt(page, 10) - 1 || 0);

  const months = Object.keys(data);
  const monthsPerPage = 3;
  const totalPages = Math.ceil(months.length / monthsPerPage);

  /**
   * Get paginated items
   * @returns - Paginated items
   */
  const getPaginatedItems = () => {
    const startIndex = currentPage * monthsPerPage;
    const endIndex = startIndex + monthsPerPage;

    if (months.length >= endIndex) {
      return months
        .slice(startIndex, endIndex)
        .reduce((result, month) => {
          result[month] = data[month];

          return result;
        }, {});
    } else {
      return {};
    }
  };

  /**
   * Handle page change
   * @param page - Page number
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate({
      search: `?page=${page + 1}`
    });
  };

  return (
    <>
      { /* Navigation button group */ }
      <Grid container sx={ { alignItems: "center", display: "flex", mb: 2 } } >
        <Grid item xs sm md lg xl>
          <Box
            sx={ {
              alignItems: "center",
              display: "flex",
              justifyContent: "flex-end"

            } }
          >
            <IconButton
              className="history-pagination-button"
              disabled={ currentPage === totalPages - 1 }
              onClick={ () => handlePageChange(currentPage + 1) }
              color="primary"
              sx={ { border: "1px solid", borderRadius: `${theme.shape.borderRadius}px`,  mr: 2 } }
            >
              <NavigateBefore />
            </IconButton>
            <Typography variant="body1" component={ "span" }>
              { formatMonth(months[currentPage * monthsPerPage + monthsPerPage - 1]) }
              &nbsp;to&nbsp;
              { formatMonth(months[currentPage * monthsPerPage]) }
            </Typography>
            <IconButton
              className="history-pagination-button"
              disabled={ currentPage === 0 }
              onClick={ () => handlePageChange(currentPage - 1) }
              color="primary"
              sx={ { border: "1px solid", borderRadius: `${theme.shape.borderRadius}px`, ml: 2 } }
            >
              <NavigateNext />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      { /* Render History items */ }
      <HistoryItems data={ getPaginatedItems() } />
    </>
  );
}

HistoryPagination.propTypes = {
  data: PropTypes.object
};

export default HistoryPagination;
